/* eslint-disable no-console */

import { isPlatformBrowser } from '@angular/common';
import type { Signal } from '@angular/core';
import { computed, inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { updateState } from '@angular-architects/ngrx-toolkit';

import type { SignalStoreFeature } from '@ngrx/signals';
import { signalStoreFeature, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';

// Интерфейс для конфигурации
export interface WithServerSyncConfig<T> {
  key: string;
  initialValue: T;
  syncOnInit?: boolean;
  serializer?: (value: T) => string;
  deserializer?: (value: string) => T;
}

// Главная универсальная функция для создания кастомной фичи
export function withServerSync<T>(config: WithServerSyncConfig<T>): SignalStoreFeature {
  const stateKey = makeStateKey<string>(`sync_${config.key}`);

  return signalStoreFeature(
    // Добавляем состояние
    withState(() => {
      const state: Record<string, T | boolean> = {};
      state[config.key] = config.initialValue;
      state[`${config.key}Synced`] = false;
      return state;
    }),

    // Добавляем computed properties
    withComputed((store) => {
      const storeTyped = store as Record<string, Signal<boolean>>;
      const computedProps: Record<string, Signal<boolean>> = {};
      computedProps[`${config.key}IsSynced`] = computed<boolean>(() => storeTyped[`${config.key}Synced`]());
      return computedProps;
    }),

    // Добавляем методы для работы с TransferState
    withMethods((store) => {
      const transferState = inject(TransferState);
      const platformId = inject(PLATFORM_ID);
      const isBrowser = isPlatformBrowser(platformId);

      const serializer = config.serializer || ((value: T) => JSON.stringify(value));
      const deserializer = config.deserializer || ((value: string) => JSON.parse(value) as T);

      const storeTyped = store as Record<string, Signal<T | boolean>>;
      const methods: Record<string, ((value: T) => void) | (() => T)> = {};

      // Обновление состояния с синхронизацией
      methods[`update${capitalize(config.key)}`] = (value: T): void => {
        try {
          const updateStateObj: Record<string, T> = {};
          updateStateObj[config.key] = value;
          updateState(store, `${config.key}/update`, updateStateObj);

          // На сервере записываем в TransferState
          if (!isBrowser) {
            const serializedValue = serializer(value);
            transferState.set(stateKey, serializedValue);
          }

          const syncState: Record<string, boolean> = {};
          syncState[`${config.key}Synced`] = true;
          updateState(store, `${config.key}/sync-status`, syncState);
        } catch (error) {
          console.error(`Error updating ${config.key}:`, error);
        }
      };

      // Синхронизация с сервера
      methods[`sync${capitalize(config.key)}FromServer`] = (): T => {
        // На сервере просто возвращаем текущее значение
        if (!isBrowser) {
          return storeTyped[config.key]() as T;
        }

        try {
          const serializedValue = transferState.get(stateKey, null);

          if (serializedValue !== null) {
            const value = deserializer(serializedValue);

            const updateStateObj: Record<string, T | boolean> = {};
            updateStateObj[config.key] = value;
            updateStateObj[`${config.key}Synced`] = true;
            updateState(store, `${config.key}/sync-from-server`, updateStateObj);

            return value;
          }

          // Если нет данных от сервера, используем начальное значение
          const initialState: Record<string, T | boolean> = {};
          initialState[config.key] = config.initialValue;
          initialState[`${config.key}Synced`] = true;
          updateState(store, `${config.key}/init`, initialState);

          return config.initialValue;
        } catch (error) {
          console.error(`Error syncing ${config.key}:`, error);

          const errorState: Record<string, T | boolean> = {};
          errorState[config.key] = config.initialValue;
          errorState[`${config.key}Synced`] = true;
          updateState(store, `${config.key}/error-fallback`, errorState);

          return config.initialValue;
        }
      };

      return methods;
    }),

    // Автоматическая синхронизация при инициализации
    withHooks({
      onInit(store) {
        if (config.syncOnInit && isPlatformBrowser(inject(PLATFORM_ID))) {
          const syncMethodName = `sync${capitalize(config.key)}FromServer`;
          const storeTyped = store as Record<string, (() => T) | undefined>;
          const syncMethod = storeTyped[syncMethodName];

          if (typeof syncMethod === 'function') {
            syncMethod();
          } else {
            console.warn(`Sync method ${syncMethodName} not found on store`);
          }
        }
      },
    }),
  );
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
